/**
 * `ChatScreen` — the live conversation view (ARCHITECTURE §7). Composes the
 * message list + composer and reflects the WS connection state. Message state
 * comes from `visitorStore` (via the session hook); sending is optimistic.
 */

import type { Message, OrgTheme } from '@flozic/shared/types';
import type { WsConnectionState } from '@flozic/shared/ws';
import { MessageList } from '../components/MessageList';
import { Composer } from '../components/Composer';

export interface ChatScreenProps {
  messages: Message[];
  connectionState: WsConnectionState;
  onSend: (body: string) => void;
  onAttach?: (file: File) => void | Promise<void>;
  /** Keystroke notifier (drives the agent-side "visitor is typing…"). */
  onTyping?: () => void;
  /** The handling agent is typing — renders the three-dot bubble. */
  agentTyping?: boolean;
  /** Org theme — drives avatars, bubble text colors, wallpaper. */
  theme?: OrgTheme;
  /** Greeting shown in the empty-state prompt. */
  welcomeMessage?: string;
}

export function ChatScreen({
  messages,
  connectionState,
  onSend,
  onAttach,
  onTyping,
  agentTyping = false,
  theme,
  welcomeMessage,
}: ChatScreenProps) {
  const reconnecting =
    connectionState === 'reconnecting' || connectionState === 'connecting';

  return (
    <div className="flex h-full flex-col">
      {reconnecting ? (
        <div className="bg-black/5 px-3 py-1 text-center text-xs text-lc-text/60">
          Reconnecting…
        </div>
      ) : null}

      <MessageList messages={messages} theme={theme} welcomeMessage={welcomeMessage} />

      {/* Agent-is-typing bubble (incoming style, three bouncing dots). */}
      {agentTyping ? (
        <div className="px-3 pb-1" aria-live="polite" aria-label="Agent is typing">
          <span className="inline-flex items-center gap-1 rounded-2xl rounded-bl-sm bg-lc-bubble-in px-3 py-2 shadow-sm">
            {[0, 150, 300].map((delay) => (
              <span
                key={delay}
                className="h-1.5 w-1.5 animate-bounce rounded-full bg-lc-text/40"
                style={{ animationDelay: `${delay}ms` }}
                aria-hidden
              />
            ))}
          </span>
        </div>
      ) : null}

      <Composer
        onSend={onSend}
        onAttach={onAttach}
        onTyping={onTyping}
        disabled={connectionState === 'closed'}
      />
    </div>
  );
}
